/**
 * Factory for creating grammar check providers
 */

import type { IGrammarCheckProvider, ProviderType, ProviderConfig } from './types'
import { ProxyProvider } from './ProxyProvider'
import { BareunProvider } from './BareunProvider'
import { NaraSpellerProvider } from './NaraSpellerProvider'

export class ProviderFactory {
  private static instances: Map<string, IGrammarCheckProvider> = new Map()

  /**
   * Create a provider instance based on type
   */
  static createProvider(config: ProviderConfig): IGrammarCheckProvider {
    switch (config.type) {
      case 'proxy':
        return new ProxyProvider()
      
      case 'bareun':
        return new BareunProvider(config)
      
      case 'naraSpeller':
        return new NaraSpellerProvider(config)
      
      default:
        throw new Error(`Unknown provider type: ${config.type}`)
    }
  }

  /**
   * Get or create a singleton provider instance
   */
  static getProvider(config: ProviderConfig): IGrammarCheckProvider {
    const key = this.getProviderKey(config)
    
    if (!this.instances.has(key)) {
      const provider = this.createProvider(config)
      this.instances.set(key, provider)
    }
    
    const provider = this.instances.get(key)!
    
    // Update configuration if changed
    provider.initialize(config)
    
    return provider
  }

  /**
   * Clear cached provider instances
   */
  static clearCache(): void {
    this.instances.clear()
  }

  /**
   * Clear a specific provider from cache
   */
  static clearProvider(type: ProviderType | string): void {
    for (const [key] of this.instances) {
      if (key.startsWith(type)) {
        this.instances.delete(key)
      }
    }
  }

  /**
   * Get all available provider types
   */
  static getAvailableProviders(): ProviderType[] {
    return ['proxy', 'bareun', 'naraSpeller']
  }

  /**
   * Check if a provider type is available
   */
  static isProviderAvailable(type: ProviderType): boolean {
    return this.getAvailableProviders().includes(type)
  }

  /**
   * Generate a unique key for provider caching
   */
  private static getProviderKey(config: ProviderConfig): string {
    if (config.type === 'proxy') {
      return 'proxy'
    }
    
    // Include API key and server URL in key for custom providers
    const parts: string[] = [config.type]
    
    if (config.serverUrl) {
      parts.push(config.serverUrl)
    }
    
    if (config.apiKey) {
      // Use first 8 chars of API key for uniqueness without exposing full key
      parts.push(config.apiKey.substring(0, 8))
    }
    
    return parts.join('-')
  }
}